import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CommonButton, CommonContainerBox } from 'gemlux-common-components';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ReactComponent as RedWarningShieldSVG } from '../../assets/img/solar-shield-warning-bold.svg';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { subscriptionActions } from '../../redux/subscription/subscriptionSlice';
import { authActions } from '../../redux/auth/authSlice';
import { RootState } from '../../redux/store';
import { toastStyles } from '../../utils/styles';
import LogoLink from '../LogoLink/LogoLink';
import { theme } from '../../theme/theme';

export type ICustomLockMessage = {
  title: string | React.ReactNode;
  description: string;
  button?: string;
  onClick?: () => void;
};

const CustomLockMessage: React.FC<ICustomLockMessage> = ({ title, description, button, onClick }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const paymentProcessing = useAppSelector((state: RootState) => state.subscription.paymentProcessing);
  const paymentError = useAppSelector((state: RootState) => state.subscription.paymentError);
  const handlePayAndUnlock = () => {
    dispatch(subscriptionActions.processPaymentAsync());
  };

  useEffect(() => {
    if (paymentProcessing === 'succeeded') {
      localStorage.clear();

      const clearCachesAndProceed = () => {
        dispatch(authActions.logOutAsync());
        dispatch(authActions.removeTokens());
      };

      if ('caches' in window) {
        caches
          .keys()
          .then((keyList) => Promise.all(keyList.map((key) => caches.delete(key))))
          .then(() => {
            clearCachesAndProceed();
            window.location.reload();
          });
      } else {
        dispatch(authActions.updateAccountStatus('active'));
        clearCachesAndProceed();
        (window as Window & typeof globalThis).location.reload();
      }
    } else if (paymentProcessing === 'failed') {
      toast.error('Automatic Payment Unsuccessfull', toastStyles.error);
    }
  }, [paymentProcessing, dispatch, navigate]);

  return (
    <CommonContainerBox sx={{ maxWidth: '250px', minHeight: '370px', borderRadius: '12px' }}>
      <LogoLink />
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography sx={{ marginTop: 4 }} variant="title4Bold" textAlign="center" color="text.primary">
          {title}
        </Typography>
        <Typography
          sx={{ marginBottom: 4 }}
          variant="tableRowRegular"
          textAlign="center"
          color={theme.palette.shades.lightDark}
        >
          {description}
        </Typography>
      </Box>
      {button && (
        <CommonButton
          variant="contained"
          color="primary"
          sx={{ backgroundColor: theme.palette.success.dark }}
          onClick={onClick ?? handlePayAndUnlock}
          padding="12px 32px"
          disabled={paymentProcessing === 'loading'}
        >
          {paymentProcessing === 'loading' ? 'Processing...' : button}
        </CommonButton>
      )}
      {paymentError && (
        <Typography variant="bodyRegular" color="error" textAlign="center">
          {paymentError}
        </Typography>
      )}
    </CommonContainerBox>
  );
};

export default CustomLockMessage;
